import type { ChartRow } from '@deepdive-ui';
import type { ChartSeries, ChartSeriesToken, TrendSeries } from '@deepdive-ui';
import type { ReasoningStep } from '@deepdive-ui';
import type { Citation, SuggestedPrompt } from '@deepdive-ui';
import { aggregate, seriesToken, X_KEY } from './aggregate';
import { USER_BRAND } from './brand';
import { bucketStart, bucketsIn } from './buckets';
import { dimensionValues, labelOf } from './dimensions';
import { KEYWORD_LABELS, KEYWORD_VALUES, type MentionRow } from './generator';
import { metrics, type MetricId } from './metrics';
import { parsePeriod, type Period } from './period';
import { pivot, pivotMetrics } from './pivot';
import { standingsFor } from './standings';

/**
 * DeepDive AI's answers.
 *
 * There is no model behind the chat. A question is matched to one of ten
 * **intents**, the figures for it come out of the same `pivot`, `aggregate`
 * and `standingsFor` calls the modules draw their cards from, and the reply is
 * assembled from them: a paragraph with the figures in bold and `[n]` markers
 * pointing at the module that owns each one, a chart in the answer's slot,
 * and the follow-ups the answer invites. That is what keeps it honest — a
 * number in a chat answer is a number a card somewhere already shows, and it
 * cannot disagree with it because it is the same call.
 *
 * Deterministic end to end: the corpus is seeded, the matching is a regex and
 * the phrasing varies only by `attempt`, which is what "Regenerate" advances.
 * Nothing here reads a clock — the period parser takes `REFERENCE_DATE`.
 *
 * The two modes differ in **depth**, per the user's call (2026-09-19). `fast`
 * answers the question in a sentence or two with one chart. `expert` breaks it
 * into steps — its reasoning trace says which — and adds the diagnosis: what
 * drives the figure, where it is worst, how it compares.
 */
export type AnswerMode = 'fast' | 'expert';

export type AnswerChart =
  | {
      kind: 'donut';
      rows: ChartRow[];
      nameKey: string;
      valueKey: string;
      colors: ChartSeriesToken[];
      centerLabel: string;
      centerValue: string;
      metric: MetricId;
    }
  | {
      kind: 'bar';
      rows: ChartRow[];
      xKey: string;
      series: ChartSeries[];
      orientation: 'horizontal' | 'vertical';
      stacked?: boolean;
      normalize?: boolean;
      metric: MetricId;
    }
  | { kind: 'line'; rows: ChartRow[]; xKey: string; series: TrendSeries[]; metric: MetricId };

export type IntentId =
  | 'compare'
  | 'negativeDrivers'
  | 'keywords'
  | 'channels'
  | 'nps'
  | 'sentiment'
  | 'engagement'
  | 'reach'
  | 'intent'
  | 'volume'
  | 'unknown';

export type Answer = {
  intent: IntentId;
  /** The paragraph. `**bold**` and `[n]` are the two forms `StreamingText` reads. */
  text: string;
  /** What the reasoning trace shows while the agent "thinks". */
  reasoning: ReasoningStep[];
  chart?: AnswerChart;
  citations: Citation[];
  followUps: SuggestedPrompt[];
  period: Period;
  /** The brand the answer is about — the chip's slug. */
  brand: string;
};

/* ------------------------------------------------------------------ helpers */

const n = (value: number) => metrics.mentions.format(Math.round(value));
/** `1 mention`, `12 mentions`. */
const mentionsOf = (value: number) => `${n(value)} mention${Math.round(value) === 1 ? '' : 's'}`;
const compact = (value: number) => metrics.engagement.format(value);
const share = (part: number, whole: number) => (whole === 0 ? 0 : (part / whole) * 100);
const pc = (part: number, whole: number) => `${share(part, whole).toFixed(0)}%`;
const signed = (value: number) => `${value >= 0 ? '+' : ''}${value.toFixed(0)}%`;

/**
 * Minimum mentions before a rate can name a "worst" — the same floor Brand
 * Health and Competitors apply, for the same reason: one negative mention is a
 * 100% rate and would win every time.
 */
const RATE_FLOOR = 3;

/** Which module a figure comes from, for the `[n]` markers. */
const MODULES = {
  brandHealth: { label: 'Brand Health', url: '#brand-health' },
  mentions: { label: 'Mentions', url: '#mentions' },
  competitors: { label: 'Competitors', url: '#competitors' },
  trends: { label: 'Trends', url: '#trends' },
  overview: { label: 'Overview', url: '#overview' },
} as const;

type ModuleKey = keyof typeof MODULES;

const cite = (index: number, module: ModuleKey, card: string): Citation => ({
  id: `c${index}`,
  index,
  url: MODULES[module].url,
  title: card,
  host: MODULES[module].label,
});

const slug = (text: string) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
const ask = (label: string): SuggestedPrompt => ({ id: slug(label), label });
const steps = (lines: string[]): ReasoningStep[] => lines.map((content, i) => ({ id: `s${i + 1}`, content }));

function countBy<K extends string>(rows: readonly MentionRow[], key: (row: MentionRow) => K | null): Map<K, number> {
  const counts = new Map<K, number>();
  for (const row of rows) {
    const k = key(row);
    if (k === null) continue;
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  return counts;
}

function groupBy<K extends string>(rows: readonly MentionRow[], key: (row: MentionRow) => K): Map<K, MentionRow[]> {
  const groups = new Map<K, MentionRow[]>();
  for (const row of rows) {
    const k = key(row);
    const list = groups.get(k);
    if (list) list.push(row);
    else groups.set(k, [row]);
  }
  return groups;
}

const negativeRate = (rows: readonly MentionRow[]) => metrics.negativeShare.reduce(rows);

/** The category with the worst negative rate, among those clearing the floor. */
function worstRate<K extends string>(groups: Map<K, MentionRow[]>): { key: K; rate: number; total: number } | undefined {
  return [...groups.entries()]
    .filter(([, rows]) => rows.length >= RATE_FLOOR)
    .map(([key, rows]) => ({ key, rate: negativeRate(rows), total: rows.length }))
    .sort((a, b) => b.rate - a.rate)[0];
}

/** Split a period in two equal halves, for "growing" and "trend" readings. */
function halves(rows: readonly MentionRow[], period: Period): [MentionRow[], MentionRow[]] {
  const mid = period.from + Math.floor((period.to - period.from) / 2);
  return [rows.filter((r) => r.day <= mid), rows.filter((r) => r.day > mid)];
}

/* ------------------------------------------------------------------- intents */

type Context = {
  question: string;
  mode: AnswerMode;
  period: Period;
  /** Every brand's rows in the period. */
  scope: readonly MentionRow[];
  /** The brand's rows in the period. */
  rows: readonly MentionRow[];
  brand: string;
  brandLabel: string;
  /** The competitor the question names, if it names one. */
  competitor: string | undefined;
  /** Picks between two phrasings by attempt, so "Regenerate" changes the words and not the figures. */
  alt: (a: string, b: string) => string;
};

type Draft = Omit<Answer, 'period' | 'brand' | 'intent'>;

/*
 * First match wins, so the order is the priority. `engagement` sits above
 * `channels` because "where does our engagement come from" is a question about
 * engagement that happens to be answered by channel — the handler reads the
 * "where" itself — while "which channels carry the most conversation" names no
 * interaction and falls through to `channels`.
 */
const INTENTS: { id: IntentId; test: RegExp }[] = [
  { id: 'compare', test: /share of voice|competitor|compar|versus|\bvs\.?\b|against|rank|market share|closest|stack up|who leads/ },
  {
    id: 'negativeDrivers',
    test: /(driv|caus|behind|why|complain|angry|unhappy|frustrat|worst)[^.]*negative|negative[^.]*(topic|driv|caus|mention|about)|complaint|pain point|what.*wrong|hurting/,
  },
  { id: 'keywords', test: /keyword|hashtag|trending|talking about|buzz|discuss|hot topic/ },
  { id: 'engagement', test: /engag|\blikes?\b|comments?|\bshares?\b|reactions?|interact/ },
  { id: 'channels', test: /channel|platform|where (?:are|do|is|does)|facebook|instagram|tiktok|youtube|twitter|linkedin|\bx\b|growing fastest|fastest.growing|source/ },
  { id: 'nps', test: /\bnps\b|net promoter|recommend|promoter|detractor/ },
  { id: 'sentiment', test: /sentiment|feel|mood|positive|negative|neutral|happy|perceiv|tone/ },
  { id: 'reach', test: /reach|audience|impression|how many people|eyeballs/ },
  { id: 'intent', test: /intent|support request|purchase|inquir|churn|praise|why (?:are|do) people|what do people want/ },
  { id: 'volume', test: /how many|volume|mention|trend|peak|spike|busiest|activity|conversation|over time|traffic/ },
];

export function detectIntent(question: string): IntentId {
  const q = question.toLowerCase();
  return INTENTS.find((intent) => intent.test.test(q))?.id ?? 'unknown';
}

/** A competitor the question names by label, else nothing. */
function detectCompetitor(question: string): string | undefined {
  const q = question.toLowerCase();
  return dimensionValues.brand.find(
    (value) => value !== USER_BRAND && q.includes(labelOf('brand', value).toLowerCase()),
  );
}

/* Sentiment split — the donut every module leads with. */
function sentiment(ctx: Context): Draft {
  const { rows, brandLabel, period, mode, alt } = ctx;
  const total = rows.length;
  const counts = [...countBy(rows, (r) => r.sentiment).entries()].sort((a, b) => b[1] - a[1]);
  const [leadValue, lead] = counts[0];
  const positive = rows.filter((r) => r.sentiment === 'positive').length;
  const negative = rows.filter((r) => r.sentiment === 'negative').length;
  const ratio =
    negative === 0
      ? 'with no negative mentions at all'
      : positive >= negative
        ? `${(positive / negative).toFixed(1)} to one in favour of positive`
        : `${(negative / positive || 0).toFixed(1)} to one against`;

  let text =
    alt(
      `**${labelOf('sentiment', leadValue)}** leads at **${n(lead)}** of **${n(total)}** conversations (${pc(lead, total)}) for ${brandLabel} ${period.phrase} [1].`,
      `Of **${n(total)}** conversations about ${brandLabel} ${period.phrase}, **${n(lead)}** (${pc(lead, total)}) are ${labelOf('sentiment', leadValue).toLowerCase()} — the largest share [1].`,
    ) +
    ` Positive runs at **${n(positive)}** (${pc(positive, total)}) against negative **${n(negative)}** (${pc(negative, total)}), ${ratio}.`;

  const reasoning = [
    `Scoping to ${brandLabel}'s conversations ${period.phrase}: ${n(total)} rows.`,
    'Counting each sentiment label once per conversation and ranking the shares.',
  ];
  const citations = [cite(1, 'brandHealth', 'Sentiment split')];

  if (mode === 'expert') {
    const topics = pivot(rows, { metric: 'mentions', xBy: 'topic', splitBy: 'sentiment', sort: 'value', sortBy: 'negative' });
    const top = topics.rows[0];
    const worst = worstRate(groupBy(rows, (r) => r.channel));
    const net = metrics.netSentiment.reduce(rows);
    if (top) {
      text += ` The negative share is concentrated in **${String(top[X_KEY])}** (${n(Number(top.negative ?? 0))} negative mentions) [2]`;
      text += worst
        ? `, and it runs hottest on **${labelOf('channel', worst.key)}**, where ${worst.rate.toFixed(0)}% of ${n(worst.total)} mentions are negative [3].`
        : '.';
    }
    text += ` Net sentiment stands at **${metrics.netSentiment.format(net)} pts**.`;
    reasoning.push(
      'The negative share is the actionable part, so I am ranking topics by negative volume rather than rate — rate would promote a topic with three mentions.',
      `Checking which channel carries the worst negative rate, with a floor of ${RATE_FLOOR} mentions so one angry post cannot top the list.`,
      'Net sentiment is positive share less negative share, in points, so it can be compared across periods.',
    );
    citations.push(cite(2, 'brandHealth', 'Topic sentiment split'), cite(3, 'mentions', 'Mentions by platform and sentiment'));
  }

  const chart: AnswerChart = {
    kind: 'donut',
    rows: counts.map(([value, count]) => ({ [X_KEY]: labelOf('sentiment', value), mentions: count })),
    nameKey: X_KEY,
    valueKey: 'mentions',
    colors: counts.map(([value]) => seriesToken('sentiment', value)),
    centerLabel: 'conversations',
    centerValue: n(total),
    metric: 'mentions',
  };

  return {
    text,
    reasoning: steps(reasoning),
    chart,
    citations,
    followUps: [
      ask('Which topics drove the most negative mentions?'),
      ask('Which channels carry the most negative mentions?'),
      ask(`How does ${brandLabel} compare with its competitors on share of voice?`),
    ],
  };
}

/* What drives the negative share — topics ranked by negative volume. */
function negativeDrivers(ctx: Context): Draft {
  const { rows, brandLabel, period, mode, alt } = ctx;
  const topics = pivot(rows, { metric: 'mentions', xBy: 'topic', splitBy: 'sentiment', sort: 'value', sortBy: 'negative' });
  const totalNegative = rows.filter((r) => r.sentiment === 'negative').length;
  const [t1, t2, t3] = topics.rows;
  const neg = (row: ChartRow | undefined) => Number(row?.negative ?? 0);

  if (!t1 || totalNegative === 0) {
    return {
      text: `There are no negative mentions of ${brandLabel} ${period.phrase} to attribute — nothing is driving a share that is zero [1].`,
      reasoning: steps([`Scoping to ${brandLabel} ${period.phrase} and counting negative mentions: none.`]),
      citations: [cite(1, 'brandHealth', 'Topic sentiment split')],
      followUps: [ask('What is the sentiment breakdown for my brand?'), ask('How has mention volume trended over the last 30 days?')],
    };
  }

  let text = alt(
    `**${String(t1[X_KEY])}** drove the most negative mentions for ${brandLabel} ${period.phrase} — **${n(neg(t1))}** of **${n(totalNegative)}** (${pc(neg(t1), totalNegative)}) [1]`,
    `The biggest source of negative mentions for ${brandLabel} ${period.phrase} is **${String(t1[X_KEY])}**, with **${n(neg(t1))}** of the **${n(totalNegative)}** complaints (${pc(neg(t1), totalNegative)}) [1]`,
  );
  text += t2 ? `, followed by **${String(t2[X_KEY])}** (${n(neg(t2))})` : '';
  text += t3 ? ` and **${String(t3[X_KEY])}** (${n(neg(t3))}).` : '.';

  const reasoning = [
    `Scoping to ${brandLabel} ${period.phrase}: ${n(rows.length)} conversations, ${n(totalNegative)} of them negative.`,
    'Ranking topics by negative volume, not rate — a topic with three mentions that all happen to be negative is not a driver.',
  ];
  const citations = [cite(1, 'brandHealth', 'Topic sentiment split')];

  if (mode === 'expert') {
    const worstTopic = worstRate(groupBy(rows, (r) => r.topic));
    const byChannel = [...countBy(rows.filter((r) => r.sentiment === 'negative'), (r) => r.channel).entries()].sort((a, b) => b[1] - a[1])[0];
    if (worstTopic) {
      text += ` By rate, **${labelOf('topic', worstTopic.key)}** is the sorest: ${worstTopic.rate.toFixed(0)}% of its ${n(worstTopic.total)} mentions are negative.`;
    }
    if (byChannel) {
      text += ` Most of the complaining happens on **${labelOf('channel', byChannel[0])}**, which carries ${n(byChannel[1])} of the negative mentions (${pc(byChannel[1], totalNegative)}) [2].`;
    }
    reasoning.push(
      `Checking the rate as well as the volume, with a floor of ${RATE_FLOOR} mentions, because the two usually name different topics and both are worth knowing.`,
      'Attributing the negative mentions to channels to say where the conversation is happening, not only what it is about.',
    );
    citations.push(cite(2, 'mentions', 'Mentions by platform and sentiment'));
  }

  return {
    text,
    reasoning: steps(reasoning),
    chart: {
      kind: 'bar',
      rows: topics.rows.slice(0, 6),
      xKey: topics.xKey,
      series: topics.series,
      orientation: 'horizontal',
      stacked: true,
      normalize: true,
      metric: 'mentions',
    },
    citations,
    followUps: [
      ask(`What is the sentiment breakdown for ${brandLabel}?`),
      ask('Which channels carry the most negative mentions?'),
      ask('What are people talking about?'),
    ],
  };
}

/* Channels — by volume, or by growth when the question asks who is rising. */
function channels(ctx: Context): Draft {
  const { rows, brandLabel, period, mode, question, alt } = ctx;
  const total = rows.length;
  const byChannel = pivot(rows, { metric: 'mentions', xBy: 'channel', sort: 'value' });
  const growth = /grow|fastest|rising|momentum|gain|accelerat/.test(question.toLowerCase());
  const reasoning = [`Scoping to ${brandLabel} ${period.phrase}: ${n(total)} conversations across ${byChannel.rows.length} channels.`];
  const citations = [cite(1, 'mentions', 'Mentions by platform and sentiment')];
  let text: string;
  let chart: AnswerChart;

  if (growth) {
    const [first, second] = halves(rows, period);
    const a = countBy(first, (r) => r.channel);
    const b = countBy(second, (r) => r.channel);
    const ranked = dimensionValues.channel
      .map((value) => {
        const before = a.get(value) ?? 0;
        const after = b.get(value) ?? 0;
        return { value, before, after, change: before === 0 ? (after > 0 ? 100 : 0) : ((after - before) / before) * 100 };
      })
      .filter((c) => c.before + c.after >= RATE_FLOOR)
      .sort((x, y) => y.change - x.change);
    const top = ranked[0];
    const next = ranked[1];
    text = top
      ? alt(
          `**${labelOf('channel', top.value)}** is growing fastest for ${brandLabel}: mentions went from **${n(top.before)}** in the first half of ${period.label} to **${n(top.after)}** in the second (${signed(top.change)}) [1].`,
          `Comparing the two halves of ${period.label}, **${labelOf('channel', top.value)}** has the most momentum for ${brandLabel} — **${n(top.before)}** mentions became **${n(top.after)}** (${signed(top.change)}) [1].`,
        ) + (next ? ` **${labelOf('channel', next.value)}** is next at ${signed(next.change)} (${n(next.before)} to ${n(next.after)}).` : '')
      : `No channel has enough mentions ${period.phrase} to call a trend — ${n(total)} conversations in total [1].`;
    reasoning.push('Splitting the period into two equal halves and comparing each channel\'s count across them — the simplest reading of "growing" that does not depend on a bucket size.');
    const series = aggregate(rows, { metric: 'mentions', from: period.from, to: period.to, splitBy: 'channel' });
    chart = { kind: 'line', rows: series.rows, xKey: series.xKey, series: series.series, metric: 'mentions' };
  } else {
    const [c1, c2, c3] = byChannel.rows;
    const count = (row: ChartRow | undefined) => Number(row?.mentions ?? 0);
    text = c1
      ? alt(
          `**${String(c1[X_KEY])}** carries the most conversation about ${brandLabel} ${period.phrase}: **${n(count(c1))}** of **${n(total)}** mentions (${pc(count(c1), total)}) [1]`,
          `Most of ${brandLabel}'s conversation ${period.phrase} happens on **${String(c1[X_KEY])}** — **${n(count(c1))}** mentions, ${pc(count(c1), total)} of **${n(total)}** [1]`,
        ) +
        (c2 ? `, ahead of **${String(c2[X_KEY])}** (${pc(count(c2), total)})` : '') +
        (c3 ? ` and **${String(c3[X_KEY])}** (${pc(count(c3), total)}).` : '.')
      : `There are no conversations about ${brandLabel} ${period.phrase} [1].`;
    reasoning.push('Ranking channels by mention count and expressing each as a share of the whole.');
    chart = { kind: 'bar', rows: byChannel.rows, xKey: byChannel.xKey, series: byChannel.series, orientation: 'vertical', metric: 'mentions' };
  }

  if (mode === 'expert' && total > 0) {
    const engagement = pivot(rows, { metric: 'engagement', xBy: 'channel', sort: 'value' });
    const e1 = engagement.rows[0];
    const worst = worstRate(groupBy(rows, (r) => r.channel));
    if (e1) {
      text += ` Engagement tells its own story: **${String(e1[X_KEY])}** generates ${compact(Number(e1.engagement ?? 0))} interactions, ${pc(Number(e1.engagement ?? 0), engagement.total)} of the total [2].`;
    }
    if (worst) {
      text += ` The worst negative rate is on **${labelOf('channel', worst.key)}**, at ${worst.rate.toFixed(0)}% of ${n(worst.total)} mentions.`;
    }
    reasoning.push(
      'Re-ranking the same channels by engagement, because volume and interaction rarely agree and the gap is where the audience actually is.',
      `Reading each channel's negative rate with a floor of ${RATE_FLOOR} mentions, so a channel with one sour post cannot be named the worst.`,
    );
    citations.push(cite(2, 'mentions', 'Mentions by platform and sentiment'));
  }

  return {
    text,
    reasoning: steps(reasoning),
    chart,
    citations,
    followUps: [
      ask('Which channels carry the most negative mentions?'),
      ask('Where does most of our engagement come from?'),
      ask('How has our mention volume trended over the last 30 days?'),
    ],
  };
}

/* Share of voice — the one cross-brand answer. */
function compare(ctx: Context): Draft {
  const { scope, brandLabel, period, mode, competitor, alt } = ctx;
  const standings = standingsFor(scope);
  const ranked = [...standings].sort((a, b) => b.mentions - a.mentions);
  const total = standings.reduce((sum, s) => sum + s.mentions, 0);
  const us = standings.find((s) => s.own);
  const others = ranked.filter((s) => !s.own);
  if (!us || total === 0) {
    return {
      text: `There are no conversations to compare ${period.phrase} — every operator is at zero [1].`,
      reasoning: steps([`Scoping every brand ${period.phrase}: no rows.`]),
      citations: [cite(1, 'competitors', 'Share of voice')],
      followUps: [ask('How does my brand compare with its competitors on share of voice?')],
    };
  }
  const rival =
    (competitor ? others.find((s) => s.value === competitor) : undefined) ??
    others.sort((a, b) => Math.abs(a.share - us.share) - Math.abs(b.share - us.share))[0];
  const rank = ranked.findIndex((s) => s.own) + 1;
  const gap = rival ? us.share - rival.share : 0;

  let text = alt(
    `${brandLabel} holds **${us.share.toFixed(1)}%** share of voice ${period.phrase} — **${n(us.mentions)}** of **${n(total)}** conversations across ${standings.length} operators, ranking **${rank} of ${standings.length}** [1].`,
    `Across ${standings.length} operators ${period.phrase}, ${brandLabel} accounts for **${n(us.mentions)}** of **${n(total)}** conversations — a **${us.share.toFixed(1)}%** share of voice and **${rank}${rank === 1 ? 'st' : rank === 2 ? 'nd' : rank === 3 ? 'rd' : 'th'} place** [1].`,
  );
  if (rival) {
    text += ` ${rival.label} is ${competitor ? 'at' : 'closest at'} **${rival.share.toFixed(1)}%**, ${Math.abs(gap).toFixed(1)} pts ${gap >= 0 ? 'behind' : 'ahead'}.`;
  }

  const reasoning = [
    `Reading every operator's conversations ${period.phrase} — this is the one question that is not scoped to ${brandLabel} alone.`,
    'Share of voice is each brand\'s mentions over the category total, so the shares add up to 100% by construction.',
  ];
  const citations = [cite(1, 'competitors', 'Share of voice')];

  if (mode === 'expert' && rival) {
    /* `Standing.positive` and `.negative` are already shares — percentages of that brand's mentions. */
    text += ` On sentiment, **${us.positive.toFixed(0)}%** of ${brandLabel}'s mentions are positive against ${rival.label}'s ${rival.positive.toFixed(0)}%, and ${us.negative.toFixed(0)}% negative against ${rival.negative.toFixed(0)}% [2]`;
    const leader = ranked[0];
    text += leader.own ? ' — the category is ours to lose.' : ` — while **${leader.label}** leads the category outright at ${leader.share.toFixed(1)}%.`;
    reasoning.push(
      'Comparing the sentiment mix of the two brands rather than raw counts, because a bigger brand always has more of everything.',
      'Naming the category leader, since second place against the closest rival and first place overall are different findings.',
    );
    citations.push(cite(2, 'competitors', 'Competitor sentiment comparison'));
  }

  return {
    text,
    reasoning: steps(reasoning),
    chart: {
      kind: 'donut',
      rows: ranked.map((s) => ({ [X_KEY]: s.label, mentions: s.mentions })),
      nameKey: X_KEY,
      valueKey: 'mentions',
      colors: ranked.map((s) => s.color),
      centerLabel: 'your share of voice',
      centerValue: `${us.share.toFixed(1)}%`,
      metric: 'mentions',
    },
    citations,
    followUps: [
      ask(`What is the sentiment breakdown for ${rival ? rival.label : 'my brand'}?`),
      ask('Which channels are growing fastest in conversation volume?'),
      ask('Which topics drove the most negative mentions?'),
    ],
  };
}

/* Mention volume over time. */
function volume(ctx: Context): Draft {
  const { rows, brandLabel, period, mode, alt } = ctx;
  const series = aggregate(rows, { metric: 'mentions', from: period.from, to: period.to });
  const starts = bucketsIn(period.from, period.to, series.bucket);
  const total = rows.length;
  const average = starts.length === 0 ? 0 : total / starts.length;
  let peakIndex = 0;
  series.rows.forEach((row, i) => {
    if (Number(row.mentions ?? 0) > Number(series.rows[peakIndex]?.mentions ?? 0)) peakIndex = i;
  });
  const peak = series.rows[peakIndex];
  const peakCount = Number(peak?.mentions ?? 0);
  const unit = series.bucket;
  /* One bucket has no peak and no halves — a day is just its count. */
  const spread = starts.length > 1;

  let text = spread
    ? alt(
        `${brandLabel} drew **${n(total)}** mentions ${period.phrase} — about **${n(average)}** a ${unit} — peaking at **${n(peakCount)}** in the ${unit} of ${String(peak?.[X_KEY] ?? '—')} [1].`,
        `Mention volume for ${brandLabel} ${period.phrase} totals **${n(total)}**, averaging **${n(average)}** per ${unit}; the busiest ${unit} was ${String(peak?.[X_KEY] ?? '—')} with **${n(peakCount)}** [1].`,
      )
    : alt(
        `${brandLabel} drew **${mentionsOf(total)}** ${period.phrase} [1].`,
        `There ${total === 1 ? 'was' : 'were'} **${mentionsOf(total)}** of ${brandLabel} ${period.phrase} [1].`,
      );
  const reasoning = [
    `Scoping to ${brandLabel} ${period.phrase}: ${n(total)} conversations.`,
    spread
      ? `Bucketing by ${unit} — ${starts.length} buckets — and reading the total, the average and the peak off the same series.`
      : 'A single day, so the count is the whole answer.',
  ];
  const citations = [cite(1, 'mentions', 'Mention volume over time')];

  if (mode === 'expert' && total > 0 && spread) {
    const [first, second] = halves(rows, period);
    const change = first.length === 0 ? 0 : ((second.length - first.length) / first.length) * 100;
    text += ` Volume is ${change >= 0 ? 'rising' : 'falling'}: the second half of the period carried **${n(second.length)}** mentions against ${n(first.length)} in the first (${signed(change)}).`;
    const peakStart = starts[peakIndex];
    const peakRows = rows.filter((r) => bucketStart(r.day, unit) === peakStart);
    const topic = [...countBy(peakRows, (r) => r.topic).entries()].sort((a, b) => b[1] - a[1])[0];
    if (topic) {
      text += ` The ${String(peak?.[X_KEY])} peak was driven by **${labelOf('topic', topic[0])}** — ${n(topic[1])} of its ${n(peakRows.length)} mentions [2].`;
    }
    reasoning.push(
      'Comparing the two halves of the period to say whether the line is going up or down, not only how high it got.',
      'Opening the peak bucket to see which topic filled it — a spike with a cause reads differently from a spike that is just Tuesday.',
    );
    citations.push(cite(2, 'brandHealth', 'Topic sentiment split'));
  }

  return {
    text,
    reasoning: steps(reasoning),
    chart: { kind: 'line', rows: series.rows, xKey: series.xKey, series: series.series, metric: 'mentions' },
    citations,
    followUps: [
      ask('Which channels are growing fastest in conversation volume?'),
      ask(`What is the sentiment breakdown for ${brandLabel}?`),
      ask('What are people talking about?'),
    ],
  };
}

/* Keywords and hashtags — what the conversation is about, in its own words. */
function keywords(ctx: Context): Draft {
  const { rows, brandLabel, period, mode, alt } = ctx;
  const top = pivot(rows, { metric: 'mentions', xBy: 'keyword', sort: 'value', limit: 8 });
  const [k1, k2, k3] = top.rows;
  const count = (row: ChartRow | undefined) => Number(row?.mentions ?? 0);
  if (!k1) {
    return {
      text: `No tagged keywords turn up in ${brandLabel}'s conversations ${period.phrase} [1].`,
      reasoning: steps([`Scoping to ${brandLabel} ${period.phrase} and counting keyword tags: none.`]),
      citations: [cite(1, 'trends', 'Trending keywords')],
      followUps: [ask('Which topics drove the most negative mentions?'), ask('How has mention volume trended over the last 30 days?')],
    };
  }

  let text = alt(
    `**${String(k1[X_KEY])}** is the most-mentioned keyword for ${brandLabel} ${period.phrase}, with **${mentionsOf(count(k1))}** [1]`,
    `People talking about ${brandLabel} ${period.phrase} reach for **${String(k1[X_KEY])}** most — **${mentionsOf(count(k1))}** [1]`,
  );
  text += k2 ? `, followed by **${String(k2[X_KEY])}** (${n(count(k2))})` : '';
  text += k3 ? ` and **${String(k3[X_KEY])}** (${n(count(k3))}).` : '.';

  const reasoning = [
    `Scoping to ${brandLabel} ${period.phrase} and keeping the rows that carry a keyword tag.`,
    'Ranking keywords by mention count; the cloud on Trends draws the same ranking.',
  ];
  const citations = [cite(1, 'trends', 'Trending keywords')];

  if (mode === 'expert') {
    const leadValue = KEYWORD_VALUES.find((value) => KEYWORD_LABELS[value] === String(k1[X_KEY]));
    const leadRows = rows.filter((r) => r.keyword === leadValue);
    const positive = share(leadRows.filter((r) => r.sentiment === 'positive').length, leadRows.length);
    const negative = share(leadRows.filter((r) => r.sentiment === 'negative').length, leadRows.length);
    const overall = share(rows.filter((r) => r.sentiment === 'positive').length, rows.length);
    text += ` Conversation around ${String(k1[X_KEY])} is ${positive.toFixed(0)}% positive and ${negative.toFixed(0)}% negative — ${positive >= overall ? 'warmer' : 'cooler'} than the brand's overall ${overall.toFixed(0)}% positive [2].`;
    const tags = pivot(rows, { metric: 'mentions', xBy: 'hashtag', sort: 'value', limit: 3 });
    const h1 = tags.rows[0];
    if (h1) text += ` On hashtags, **${String(h1[X_KEY])}** leads with ${n(count(h1))} uses [3].`;
    reasoning.push(
      'Reading the sentiment mix inside the leading keyword and comparing it with the brand\'s overall mix, so "most mentioned" is not mistaken for "most liked".',
      'Checking the hashtags separately — they are a smaller, noisier signal, but they are what people choose to amplify.',
    );
    citations.push(cite(2, 'brandHealth', 'Sentiment split'), cite(3, 'trends', 'Trending hashtags'));
  }

  return {
    text,
    reasoning: steps(reasoning),
    chart: { kind: 'bar', rows: top.rows, xKey: top.xKey, series: top.series, orientation: 'horizontal', metric: 'mentions' },
    citations,
    followUps: [
      ask('Which topics drove the most negative mentions?'),
      ask(`What is the sentiment breakdown for ${brandLabel}?`),
      ask('Which channels carry the most conversation?'),
    ],
  };
}

/*
 * Engagement — by interaction type, or by channel when the question asks
 * where it comes from. The two are the same total cut two ways.
 */
function engagement(ctx: Context): Draft {
  const { rows, brandLabel, period, mode, question, alt } = ctx;
  const total = metrics.engagement.reduce(rows);
  const byWhere = /where|channel|platform|source|come from|from which/.test(question.toLowerCase());
  /* Three types, not four — `reactions` carries likes. See the metric. */
  const types = pivotMetrics(rows, { metrics: ['reactions', 'comments', 'shares'] });
  const byChannel = pivot(rows, { metric: 'engagement', xBy: 'channel', sort: 'value' });
  const ranked = [...types.rows].sort((a, b) => Number(b.value ?? 0) - Number(a.value ?? 0));
  const [e1, e2] = ranked;
  const [c1, c2] = byChannel.rows;
  const typeCount = (row: ChartRow | undefined) => Number(row?.value ?? 0);
  const channelCount = (row: ChartRow | undefined) => Number(row?.engagement ?? 0);
  const lower = (row: ChartRow | undefined) => String(row?.[X_KEY] ?? '—').toLowerCase();

  let text: string;
  const reasoning = [`Scoping to ${brandLabel} ${period.phrase} and summing every interaction: ${compact(total)}.`];
  const citations = [cite(1, byWhere ? 'mentions' : 'brandHealth', byWhere ? 'Mentions by platform and sentiment' : 'Engagement by type')];

  if (byWhere) {
    text = c1
      ? alt(
          `Most of ${brandLabel}'s engagement comes from **${String(c1[X_KEY])}**: **${compact(channelCount(c1))}** of **${compact(total)}** interactions ${period.phrase} (${pc(channelCount(c1), total)}) [1]`,
          `**${String(c1[X_KEY])}** produces the most engagement for ${brandLabel} ${period.phrase} — **${compact(channelCount(c1))}** of **${compact(total)}** interactions (${pc(channelCount(c1), total)}) [1]`,
        ) + (c2 ? `, with **${String(c2[X_KEY])}** next at ${pc(channelCount(c2), total)}.` : '.')
      : `There is no engagement to attribute for ${brandLabel} ${period.phrase} [1].`;
    reasoning.push('Attributing the interactions to the channel each conversation happened on and ranking the channels.');
  } else {
    text =
      alt(
        `${brandLabel}'s conversations generated **${compact(total)}** interactions ${period.phrase} [1]; **${lower(e1)}** account for the largest share at ${pc(typeCount(e1), total)} (**${compact(typeCount(e1))}**)`,
        `Across ${period.label}, mentions of ${brandLabel} drew **${compact(total)}** interactions [1], led by **${lower(e1)}** at ${pc(typeCount(e1), total)} (**${compact(typeCount(e1))}**)`,
      ) + (e2 ? `, then ${lower(e2)} (${pc(typeCount(e2), total)}).` : '.');
    reasoning.push('Splitting the total into its four interaction types, which add back to it exactly, and ranking them.');
  }

  if (mode === 'expert' && total > 0) {
    const rate = metrics.engagementRate.reduce(rows);
    text += ` That is an engagement rate of **${rate.toFixed(1)}%** against reach [2]`;
    if (byWhere) {
      text += e1 ? `, and across every channel **${lower(e1)}** are the commonest interaction (${pc(typeCount(e1), total)}) [3].` : '.';
    } else {
      text += c1 ? `, and **${String(c1[X_KEY])}** produces ${pc(channelCount(c1), byChannel.total)} of all interactions [3].` : '.';
    }
    reasoning.push(
      'Dividing engagement by reach as a ratio of totals, not a mean of per-post rates — one viral post would otherwise drag the average.',
      byWhere
        ? 'Cutting the same total by interaction type, so "where" comes with "what kind".'
        : 'Attributing the interactions to channels, because "where" is usually the more useful half of "how much".',
    );
    citations.push(
      cite(2, 'overview', 'Engagement rate'),
      cite(3, byWhere ? 'brandHealth' : 'mentions', byWhere ? 'Engagement by type' : 'Mentions by platform and sentiment'),
    );
  }

  return {
    text,
    reasoning: steps(reasoning),
    chart: byWhere
      ? { kind: 'bar', rows: byChannel.rows, xKey: byChannel.xKey, series: byChannel.series, orientation: 'vertical', metric: 'engagement' }
      : { kind: 'bar', rows: types.rows, xKey: types.xKey, series: types.series, orientation: 'vertical', metric: 'engagement' },
    citations,
    followUps: [
      ask('Which channels carry the most conversation?'),
      ask('How many people did our mentions reach?'),
      ask(`What is the sentiment breakdown for ${brandLabel}?`),
    ],
  };
}

/* Reach — how many people the mentions got in front of. */
function reach(ctx: Context): Draft {
  const { rows, brandLabel, period, mode, alt } = ctx;
  const total = metrics.reach.reduce(rows);
  const byChannel = pivot(rows, { metric: 'reach', xBy: 'channel', sort: 'value' });
  const [c1, c2] = byChannel.rows;
  const count = (row: ChartRow | undefined) => Number(row?.reach ?? 0);

  let text = alt(
    `Mentions of ${brandLabel} reached **${compact(total)}** people ${period.phrase} [1]`,
    `${brandLabel}'s conversations ${period.phrase} put the brand in front of **${compact(total)}** people [1]`,
  );
  text += c1 ? `, with **${String(c1[X_KEY])}** delivering ${pc(count(c1), total)} of it (**${compact(count(c1))}**)` : '';
  text += c2 ? ` and **${String(c2[X_KEY])}** ${pc(count(c2), total)}.` : '.';

  const reasoning = [
    `Scoping to ${brandLabel} ${period.phrase} and summing reach across ${n(rows.length)} conversations.`,
    'Attributing reach to channels.',
  ];
  const citations = [cite(1, 'overview', 'Total unique audience')];

  if (mode === 'expert' && total > 0) {
    const rate = metrics.engagementRate.reduce(rows);
    const best = [...groupBy(rows, (r) => r.channel).entries()]
      .filter(([, group]) => group.length >= RATE_FLOOR)
      .map(([channel, group]) => ({ channel, rate: metrics.engagementRate.reduce(group) }))
      .sort((a, b) => b.rate - a.rate)[0];
    text += ` Reach converts to interaction at **${rate.toFixed(1)}%** overall [2]`;
    text += best ? `; **${labelOf('channel', best.channel)}** converts best, at ${best.rate.toFixed(1)}%.` : '.';
    reasoning.push(
      'Reading reach against engagement, because an audience that does not react is a number rather than an audience.',
      `Ranking channels by engagement rate with a floor of ${RATE_FLOOR} mentions.`,
    );
    citations.push(cite(2, 'overview', 'Engagement rate'));
  }

  return {
    text,
    reasoning: steps(reasoning),
    chart: { kind: 'bar', rows: byChannel.rows, xKey: byChannel.xKey, series: byChannel.series, orientation: 'vertical', metric: 'reach' },
    citations,
    followUps: [
      ask('Where does most of our engagement come from?'),
      ask('Which channels are growing fastest in conversation volume?'),
      ask('How has our mention volume trended over the last 30 days?'),
    ],
  };
}

/* Intent — why people are talking. */
function intent(ctx: Context): Draft {
  const { rows, brandLabel, period, mode, alt } = ctx;
  const total = rows.length;
  const byIntent = pivot(rows, { metric: 'mentions', xBy: 'intent', sort: 'value' });
  const [i1, i2, i3] = byIntent.rows;
  const count = (row: ChartRow | undefined) => Number(row?.mentions ?? 0);

  let text = i1
    ? alt(
        `**${String(i1[X_KEY])}** is the most common intent behind mentions of ${brandLabel} ${period.phrase}: **${n(count(i1))}** of **${n(total)}** (${pc(count(i1), total)}) [1]`,
        `Most people mentioning ${brandLabel} ${period.phrase} are there for **${String(i1[X_KEY]).toLowerCase()}** — **${n(count(i1))}** of **${n(total)}** conversations (${pc(count(i1), total)}) [1]`,
      )
    : `There are no conversations about ${brandLabel} ${period.phrase} to read an intent from [1].`;
  if (i1) {
    text += i2 ? `, followed by **${String(i2[X_KEY])}** (${pc(count(i2), total)})` : '';
    text += i3 ? ` and **${String(i3[X_KEY])}** (${pc(count(i3), total)}).` : '.';
  }

  const reasoning = [
    `Scoping to ${brandLabel} ${period.phrase}: ${n(total)} conversations.`,
    'Ranking intents by count and expressing each as a share of the whole.',
  ];
  const citations = [cite(1, 'mentions', 'Intent-based mentions')];

  if (mode === 'expert' && total > 0) {
    const worst = worstRate(groupBy(rows, (r) => r.intent));
    if (worst) {
      text += ` **${labelOf('intent', worst.key)}** carries the highest negative rate, at ${worst.rate.toFixed(0)}% of ${n(worst.total)} mentions [2] — the intent most worth a reply.`;
    }
    reasoning.push(`Reading each intent's negative rate with a floor of ${RATE_FLOOR}, because the loudest intent and the angriest one are usually different.`);
    citations.push(cite(2, 'brandHealth', 'Sentiment split'));
  }

  return {
    text,
    reasoning: steps(reasoning),
    chart: { kind: 'bar', rows: byIntent.rows.slice(0, 6), xKey: byIntent.xKey, series: byIntent.series, orientation: 'horizontal', metric: 'mentions' },
    citations,
    followUps: [
      ask('Which topics drove the most negative mentions?'),
      ask(`What is the sentiment breakdown for ${brandLabel}?`),
      ask('Where does most of our engagement come from?'),
    ],
  };
}

/* NPS. */
function nps(ctx: Context): Draft {
  const { rows, brandLabel, period, mode, alt } = ctx;
  const total = rows.length;
  const score = metrics.npsNet.reduce(rows);
  const promoters = rows.filter((r) => r.npsScore >= 9).length;
  const detractors = rows.filter((r) => r.npsScore <= 6).length;

  let text = alt(
    `${brandLabel}'s NPS stands at **${metrics.npsNet.format(score)}** ${period.phrase}: ${pc(promoters, total)} promoters against ${pc(detractors, total)} detractors across **${n(total)}** scored conversations [1].`,
    `Across **${n(total)}** scored conversations ${period.phrase}, ${brandLabel} scores an NPS of **${metrics.npsNet.format(score)}** — ${pc(promoters, total)} promoters, ${pc(detractors, total)} detractors [1].`,
  );
  const reasoning = [
    `Scoping to ${brandLabel} ${period.phrase}: ${n(total)} conversations, each carrying a 0–10 score.`,
    'Promoters score 9 or 10, detractors 6 or under; NPS is the gap between their shares.',
  ];
  const citations = [cite(1, 'brandHealth', 'NPS')];
  const series = aggregate(rows, { metric: 'npsNet', from: period.from, to: period.to });

  if (mode === 'expert' && total > 0) {
    const [first, second] = halves(rows, period);
    const before = metrics.npsNet.reduce(first);
    const after = metrics.npsNet.reduce(second);
    const gap = after - before;
    text += ` It is ${gap >= 0 ? 'improving' : 'slipping'}: **${metrics.npsNet.format(before)}** in the first half of the period became **${metrics.npsNet.format(after)}** in the second (${gap >= 0 ? '+' : ''}${gap.toFixed(1)} pts).`;
    const worst = worstRate(groupBy(rows.filter((r) => r.npsScore <= 6), (r) => r.topic));
    const topic = [...countBy(rows.filter((r) => r.npsScore <= 6), (r) => r.topic).entries()].sort((a, b) => b[1] - a[1])[0];
    if (topic) text += ` Detractors cluster around **${labelOf('topic', topic[0])}** (${n(topic[1])} of ${n(detractors)}) [2].`;
    reasoning.push(
      'Comparing the two halves of the period in points, never as a percentage change — a score that crosses zero has no meaningful percentage.',
      'Opening the detractors to see which topic they share, which is the part a team can act on.',
    );
    if (worst) reasoning.push('Rates are read with the usual floor of three mentions.');
    citations.push(cite(2, 'brandHealth', 'Topic sentiment split'));
  }

  return {
    text,
    reasoning: steps(reasoning),
    chart: { kind: 'line', rows: series.rows, xKey: series.xKey, series: series.series, metric: 'npsNet' },
    citations,
    followUps: [
      ask(`What is the sentiment breakdown for ${brandLabel}?`),
      ask('Which topics drove the most negative mentions?'),
      ask('How does my brand compare with its competitors on share of voice?'),
    ],
  };
}

/* Nothing matched. */
function unknown(ctx: Context): Draft {
  const { brandLabel } = ctx;
  const rivals = dimensionValues.brand.filter((v) => v !== USER_BRAND).map((v) => labelOf('brand', v));
  return {
    text:
      `I can answer questions about ${brandLabel}'s mentions, sentiment, channels, topics, keywords, engagement, reach and NPS — and how it compares with ${rivals.slice(0, -1).join(', ')} and ${rivals[rivals.length - 1]}. ` +
      'Name a period to narrow it ("last month", "since July", "in Q2"); otherwise I read all time. Here are a few to start with.',
    reasoning: steps(['I could not map that question to one of the analyses I can run, so I am listing what is available.']),
    citations: [],
    followUps: [
      ask('What is the sentiment breakdown for my top brand?'),
      ask('Which topics drove the most negative mentions last month?'),
      ask('How does my brand compare with its competitors on share of voice?'),
    ],
  };
}

const HANDLERS: Record<IntentId, (ctx: Context) => Draft> = {
  compare,
  negativeDrivers,
  keywords,
  channels,
  nps,
  sentiment,
  engagement,
  reach,
  intent,
  volume,
  unknown,
};

/**
 * Answer a question over the corpus.
 *
 * `rows` is every row the app reads — with Tune sentiment's overrides applied,
 * so a correction saved on Competitors changes what the chat says too.
 * `attempt` is how many times the answer has been regenerated; it changes the
 * phrasing and nothing else.
 */
export function answerQuestion(
  question: string,
  mode: AnswerMode,
  rows: readonly MentionRow[],
  attempt = 0,
): Answer {
  const intent = detectIntent(question);
  const period = parsePeriod(question);
  const competitor = detectCompetitor(question);
  /*
   * "my brand", "our brand" and a question that names nobody are all about us.
   * A question that names a competitor is about them — unless it is a
   * comparison, which is about both and stays scoped to us with `competitor`
   * as the other side.
   */
  const brand = competitor !== undefined && intent !== 'compare' ? competitor : USER_BRAND;
  const scope = rows.filter((row) => row.day >= period.from && row.day <= period.to);
  const brandRows = scope.filter((row) => row.brand === brand);
  const ctx: Context = {
    question,
    mode,
    period,
    scope,
    rows: brandRows,
    brand,
    brandLabel: labelOf('brand', brand),
    competitor,
    alt: (a, b) => (attempt % 2 === 0 ? a : b),
  };

  /*
   * An empty window answers itself, whatever the intent — every handler would
   * otherwise have to guard the same thing. `compare` reads the whole category
   * and has its own empty case; `unknown` reads nothing.
   */
  if (brandRows.length === 0 && intent !== 'compare' && intent !== 'unknown') {
    return {
      intent,
      period,
      brand,
      text: `I found no conversations about ${ctx.brandLabel} ${period.phrase}, so there is nothing to ${intent === 'volume' ? 'count' : 'break down'}. The corpus runs ${parsePeriod('all time').label.replace('all time ', '')}; try a period inside it.`,
      reasoning: steps([`Scoping to ${ctx.brandLabel} ${period.phrase}: no rows.`]),
      citations: [],
      followUps: [
        ask(`${question.replace(/\.$/, '')} over the last 30 days`),
        ask('What is the sentiment breakdown for my top brand?'),
      ],
    };
  }

  const draft = HANDLERS[intent](ctx);
  return { intent, period, brand, ...draft };
}
